/**
 * Self-contained decay logic for the landing page.
 * Mirrors @run2sale/shared decay constants — no cross-package dependency.
 */

export interface DecayTier {
  fromMinutes: number;
  toMinutes: number;
  discountPercent: number;
}

export interface DecayCurve {
  tiers: DecayTier[];
  expiresAfterMinutes: number;
}

export type DecayStatus = 'active' | 'expired';

export interface DecayResult {
  discountPercent: number;
  status: DecayStatus;
  secondsUntilNextDrop: number | null;
  nextDiscountPercent: number | null;
  currentTierIndex: number | null;
  elapsedSeconds: number;
  totalSeconds: number;
  progress: number;
}

export const DEFAULT_DECAY_CURVE: DecayCurve = {
  tiers: [
    { fromMinutes: 0, toMinutes: 5, discountPercent: 50 },
    { fromMinutes: 5, toMinutes: 15, discountPercent: 40 },
    { fromMinutes: 15, toMinutes: 30, discountPercent: 20 },
    { fromMinutes: 30, toMinutes: 60, discountPercent: 10 },
  ],
  expiresAfterMinutes: 60,
};

export function calculateDecay(
  curve: DecayCurve,
  elapsedSeconds: number,
): DecayResult {
  const totalSeconds = curve.expiresAfterMinutes * 60;
  const elapsedMinutes = elapsedSeconds / 60;
  const progress = Math.min(elapsedSeconds / totalSeconds, 1);

  if (elapsedSeconds >= totalSeconds) {
    return {
      discountPercent: 0,
      status: 'expired',
      secondsUntilNextDrop: null,
      nextDiscountPercent: null,
      currentTierIndex: null,
      elapsedSeconds,
      totalSeconds,
      progress: 1,
    };
  }

  for (let i = 0; i < curve.tiers.length; i++) {
    const tier = curve.tiers[i];
    if (elapsedMinutes >= tier.fromMinutes && elapsedMinutes < tier.toMinutes) {
      const secondsUntilEnd = tier.toMinutes * 60 - elapsedSeconds;
      const nextTier = curve.tiers[i + 1];
      return {
        discountPercent: tier.discountPercent,
        status: 'active',
        secondsUntilNextDrop: secondsUntilEnd,
        nextDiscountPercent: nextTier?.discountPercent ?? null,
        currentTierIndex: i,
        elapsedSeconds,
        totalSeconds,
        progress,
      };
    }
  }

  return {
    discountPercent: 0,
    status: 'expired',
    secondsUntilNextDrop: null,
    nextDiscountPercent: null,
    currentTierIndex: null,
    elapsedSeconds,
    totalSeconds,
    progress: 1,
  };
}

export function getTierColor(tierIndex: number | null): string {
  switch (tierIndex) {
    case 0: return '#22C55E';
    case 1: return '#EAB308';
    case 2: return '#F97316';
    case 3: return '#EF4444';
    default: return '#6B7280';
  }
}

export function getTierLabel(tierIndex: number | null): string {
  switch (tierIndex) {
    case 0: return 'Best Deal';
    case 1: return 'Great Deal';
    case 2: return 'Good Deal';
    case 3: return 'Last Chance';
    default: return 'Expired';
  }
}
